import React, { useState, useEffect, useContext } from "react";
import Seo from "../../../shared/ui/Seo";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../../cart/context/CartContext";
import { getAddresses } from "../../account/api";
import CheckoutAddAddressModal from "./CheckoutAddAddressModal";
import { onePayCheckout } from "../payments/onePay";
import SelectAddressModal from "./SelectAddressModal";
import { getBuyNow, getBuyNowTotal, clearBuyNow } from "../api";
import { getProductsByListOfIds } from "../../products/api";
import { createOrder } from "../api";
import { getCoupondetails, getAvailableCoupons } from "../../coupons/api";
import { kokoPay } from "../payments/kokoPay";
import { toast } from "react-toastify";
import { AuthContext } from "../../auth/context/AuthContext";
import { S3_URL_PRODUCTS } from "../../../shared/utils/constants";
import { parseWeight } from "../utils/deliveryFee";
import { getEffectivePrice, formatPrice } from "../utils/pricing";
import useDeliveryFee from "../hooks/useDeliveryFee";
import useKokoTaxRate from "../hooks/useKokoTaxRate";
import DeliveryFeeAmount from "./DeliveryFeeAmount";
import CheckoutSteps from "./CheckoutSteps";
import PaymentOptions from "./PaymentOptions";
import { CouponField, CouponTicket } from "../../coupons/components/CouponTicket";
import "../../../shared/styles/cart-checkout.css";

const RegisteredCheckout = () => {
  // Payment-related state
  const [selectedPayment, setSelectedPayment] = useState("payment3");
  const { cart, isCartLoaded } = useCart();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isAuthenticated, user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [isCardPaymentsExpanded, setIsCardPaymentsExpanded] = useState(false);
  const [selectedCardPayment, setSelectedCardPayment] = useState("");
  // State for addresses
  const [addresses, setAddresses] = useState([]);
  const [selectedShippingId, setSelectedShippingId] = useState(null);
  const [selectedBillingId, setSelectedBillingId] = useState(null);
  // Override checkbox: when checked, billing is used as shipping
  const [useSameAddress, setUseSameAddress] = useState(false);
  // Store previous shipping id to restore when unchecked
  const [previousShippingId, setPreviousShippingId] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  // Modal state for adding/changing addresses
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showBillingSelectModal, setShowBillingSelectModal] = useState(false);
  const [showShippingSelectModal, setShowShippingSelectModal] = useState(false);
  const [customerNote, setCustomerNote] = useState("");
  const [couponDetails, setCouponDetails] = useState(null);
  const [availableCoupons, setAvailableCoupons] = useState([]);

  useEffect(() => {
    return () => {
      if (window.location.pathname !== "/checkout") {
        clearBuyNow();
      }
    };
  }, [clearBuyNow]);

  const fetchAvailableCoupons = async () => {
    try {
      const data = await getAvailableCoupons();
      setAvailableCoupons(data);
    } catch (error) {
      console.error("Error fetching available coupons:", error);
    }
  };
  useEffect(() => {
    if (isAuthenticated && items.length > 0) {
      fetchAvailableCoupons();
    }
  }, [isAuthenticated, items]);

  useEffect(() => {
    if (isCartLoaded && !(cart.length > 0 || getBuyNow())) {
      navigate("/");
    }
  }, [cart, isCartLoaded, navigate]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const buyNowProduct = getBuyNow();
        if (buyNowProduct) {
          setItems([buyNowProduct]);
          setTotal(getBuyNowTotal());
          return;
        }
        const cartItems = cart;
        if (cartItems.length > 0) {
          const productIds = cartItems.map((item) => item.id.productId);
          const response = await getProductsByListOfIds(productIds);
          const productsFromApi = Array.isArray(response)
            ? response
            : response.products;
          if (!productsFromApi || productsFromApi.length === 0) {
            return [];
          }
          const productData = cartItems.map((item) => {
            const prodId = item.id.productId;
            const productWrapper = productsFromApi.find(
              (p) => p.product.id === prodId
            );
            if (!productWrapper) return null;
            const product = productWrapper.product;
            return {
              id: product.id,
              name: product.name,
              images: product.images,
              salePrice: product.salePrice,
              regularPrice: product.regularPrice,
              stockQuantity: product.stockQuantity,
              quantity: item.quantity,
              sku: product.sku,
              configuration: product.configuration || null,
              nonCod: product.nonCod || false,
              slug: product.slug,
              weight: parseWeight(product.weight),
            };
          });
          const filteredProducts = productData.filter((p) => p !== null);
          setItems(filteredProducts);
          setTotal(
            filteredProducts.reduce((sum, item) => {
              const config = item.configuration;
              const configDiscount =
                config?.Sale?.collectionConfiguration?.discount || 0;
              const discountActive =
                config &&
                configDiscount &&
                config?.Sale?.configStatus === "active";

              let effectivePrice;
              if (discountActive) {
                if (item.salePrice) {
                  // Case 1: Active config with salePrice available; apply discount on salePrice.
                  effectivePrice = item.salePrice * (1 - configDiscount / 100);
                } else {
                  // Case 3: Active config but no salePrice; calculate effective price from regularPrice.
                  effectivePrice =
                    item.regularPrice * (1 - configDiscount / 100);
                }
              } else {
                // Cases 2 & 4: No active config; use salePrice if available, otherwise regularPrice.
                effectivePrice = item.salePrice || item.regularPrice;
              }
              return sum + item.quantity * effectivePrice;
            }, 0)
          );

        }
      } catch (error) {
        console.error("Error loading cart:", error);
        navigate("/");
      }
    };
    fetchItems();
  }, [cart, navigate]);

  const { kokoTaxRate, kokoTaxRateStatus } = useKokoTaxRate();
  const {
    deliveryFee,
    deliveryFeeStatus,
    loadDeliveryFee,
    ensureDeliveryFee,
  } = useDeliveryFee(
    items.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      weight: item.weight,
    }))
  );
  useEffect(() => {
    const fetchAddresses = async () => {
      try {
        const res = await getAddresses();
        if (res) {
          setAddresses(res.addresses || []);
          setSelectedShippingId(res.defaultShippingId);
          setSelectedBillingId(res.defaultBillingId);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error);
        toast.error("Unable to fetch addresses.");
      }
    };
    if (localStorage.getItem("couponDetails")) {
      setCouponDetails(JSON.parse(localStorage.getItem("couponDetails")));
    }
    fetchAddresses();
  }, []);

  useEffect(() => {
    if (useSameAddress && selectedBillingId) {
      setPreviousShippingId(selectedShippingId);
      setSelectedShippingId(selectedBillingId);
    }
  }, [useSameAddress, selectedBillingId]);

  const handlePaymentChange = (e) => {
    const payment = e.target.value;
    setSelectedPayment(payment);
    // When Card Payments is selected, expand its options.
    if (payment === "cardPayments") {
      setIsCardPaymentsExpanded(true);
    } else {
      setIsCardPaymentsExpanded(false);
    }
  };
  const handleCardPaymentChange = (e) => {
    setSelectedCardPayment(e.target.value);
  };

  const handleCustomerNoteChange = (e) => {
    setCustomerNote(e.target.value);
  };

  // Override checkbox change handler
  const handleUseSameAddressChange = (e) => {
    const isChecked = e.target.checked;
    setUseSameAddress(isChecked);
    if (isChecked && selectedBillingId) {
      // Save the current shipping id before overriding
      setPreviousShippingId(selectedShippingId);
      setSelectedShippingId(selectedBillingId);
    } else {
      // When unchecked, restore previous shipping id if available
      if (previousShippingId) {
        setSelectedShippingId(previousShippingId);
      }
    }
  };

  // A single non-COD item in the order rules out cash on delivery.
  const hasNonCodItem = items.some((item) => item.nonCod);

  useEffect(() => {
    if (hasNonCodItem && selectedPayment === "payment3") {
      setSelectedPayment("cardPayments");
      setIsCardPaymentsExpanded(true);
      setSelectedCardPayment("payment5");
    }
  }, [hasNonCodItem, selectedPayment]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    if (addresses.length === 0) {
      toast.error("Please add a new address to proceed.");
      return;
    }

    let finalShippingId = selectedShippingId;
    if (!finalShippingId && selectedBillingId) {
      const useBillingForShipping = window.confirm(
        "No shipping address selected. Would you like to use your billing address as your shipping address?"
      );
      if (useBillingForShipping) {
        finalShippingId = selectedBillingId;
        setSelectedShippingId(selectedBillingId);
      } else {
        toast.error(
          "Please add/select a shipping address before placing your order."
        );
        return;
      }
    }
    if (!selectedBillingId || !finalShippingId) {
      toast.error(
        "Please add and select both billing and shipping addresses before placing your order."
      );
      return;
    }
    if (hasNonCodItem && selectedPayment === "payment3") {
      toast.error(
        "Cash on delivery isn't available for this order. Please choose another payment method."
      );
      return;
    }
    if (selectedPayment === "payment6" && kokoTaxRate === null) {
      toast.error("Unable to load the KOKO Pay tax rate. Please try again.");
      return;
    }
    setIsSubmitting(true);
    const fee = await ensureDeliveryFee();
    if (fee === null) {
      setIsSubmitting(false);
      return;
    }
    const paymentMethodMap = {
      payment3: "COD",
      payment4: "payhere",
      payment5: "onePay",
      payment6: "kokoPay",
    };

    const lineItems = items.map((item) => ({
      productId: item.id,
      quantity: item.quantity,
      salePrice: item.salePrice || item.regularPrice,
    }));

    const couponDTO = {
      couponId: couponDetails ? couponDetails.couponId : null,
      couponCode: couponCode,
      discountPercentage: couponDetails ? couponDetails.discountedValue : null,
      discountAmount: discount,
    };

    const orderDTO = {
      paymentMethod:
        selectedPayment === "cardPayments"
          ? paymentMethodMap[selectedCardPayment]
          : paymentMethodMap[selectedPayment],
      customerId: user ? user.id : null,
      billingId: selectedBillingId,
      shippingId: finalShippingId,
      lineItems: lineItems,
      couponLines: null,
      customerNote: customerNote || null,
      totalPrice: total.toFixed(2),
      deliveryFee: fee.toFixed(2),
      discount: discount,
      couponOrder: couponDetails ? couponDTO : null,
      platform: "WEB",
      kokoTax: kokoTax.toFixed(2),
    };
    try {
      const response = await createOrder(orderDTO);

      if (
        selectedPayment === "cardPayments" &&
        selectedCardPayment === "payment4"
      ) {
        const { customOrderId, payHereDTO } = response.object;
        const payment = {
          sandbox: process.env.REACT_APP_PAYHERE_SANDBOX,
          merchant_id: payHereDTO.merchantId,
          return_url: payHereDTO.returnUrl,
          cancel_url: payHereDTO.cancelUrl,
          notify_url: payHereDTO.notifyUrl,
          order_id: customOrderId,
          items: payHereDTO.orderNumber,
          amount: payHereDTO.amount,
          currency: payHereDTO.currency,
          hash: payHereDTO.hash,
          first_name: defaultBillingAddress.firstName,
          last_name: defaultBillingAddress.lastName,
          email: defaultBillingAddress.email,
          phone: defaultBillingAddress.phone,
          address: defaultBillingAddress.address_1,
          city: defaultBillingAddress.city,
          country: "Sri Lanka",
          delivery_address: defaultShippingAddress.address_1,
          delivery_city: defaultShippingAddress.city,
          delivery_country: "Sri Lanka",
          custom_1: "WEB",
          custom_2: "",
        };
        sessionStorage.setItem("orderId", response.object.customOrderId);
        window.payhere.onCompleted = function (orderId) {
        };
        window.payhere.onDismissed = function () {
        };
        window.payhere.onError = function (error) {
          console.error("Payment error:", error);
        };
        window.payhere.startPayment(payment);
      } else if (
        selectedPayment === "cardPayments" &&
        selectedCardPayment === "payment5"
      ) {
        const { customOrderId, onePayDTO } = response.object;
        const onePayData = {
          currency: "LKR",
          app_id: onePayDTO.appId,
          amount: onePayDTO.amount,
          reference: `Bigmall-${onePayDTO.generatedOrderId}`,
          customer_first_name: defaultBillingAddress.firstName,
          customer_last_name: defaultBillingAddress.lastName,
          customer_phone_number: defaultBillingAddress.phone,
          customer_email: defaultBillingAddress.email,
          transaction_redirect_url: onePayDTO.transactionRedirectUrl,
          //apptoken: process.env.REACT_APP_ONEPAY_APP_TOKEN,
          additional_data: customOrderId,
          hash: onePayDTO.hash,
        };
        try {
          const responseOnepay = await onePayCheckout(
            onePayData,
            onePayDTO.appToken
          );
          if (responseOnepay.status === 200) {
            sessionStorage.setItem("orderId", response.object.customOrderId);
            sessionStorage.setItem("onePayTransactionId", responseOnepay.data.ipg_transaction_id);
            const url = responseOnepay.data.gateway.redirect_url;
            window.location.href = url;
          } else {
            console.error("OnePay request failed:", responseOnepay);
            toast.error("OnePay payment failed.");
          }
        } catch (error) {
          console.error("OnePay request error:", error);
          toast.error("OnePay payment request failed.");
        }
      } else if (selectedPayment === "payment6") {
        const { kokoDTO } = response.object;

        const kokoPayData = {
          orderId: kokoDTO.orderId,
          amount: kokoDTO.amount,
          description: kokoDTO.description,
          firstName: kokoDTO.firstName,
          lastName: kokoDTO.firstName,
          email: kokoDTO.email,
          reference: kokoDTO.reference,
          phone: kokoDTO.phone,
          returnUrl: kokoDTO.returnUrl,
          cancelUrl: kokoDTO.cancelUrl,
          responseUrl: kokoDTO.responseUrl,
        };

        sessionStorage.setItem("orderId", kokoDTO.orderId);

        kokoPay(kokoPayData);
      } else {
        toast.success("Order submitted successfully!");
        sessionStorage.setItem("orderId", response.object.customOrderId);
        const redirectUrl = response.object.orderSuccessUrl;
        window.location.href = redirectUrl;
      }
    } catch (error) {
      console.error("Error submitting order:", error);
      let errorMessage = "Something went wrong!";
      if (error.response && error.response.data) {
        errorMessage = error.response.data.message
          ? error.response.data.message
          : JSON.stringify(error.response.data);
      } else if (error.message) {
        errorMessage = error.message;
      }
      toast.error(`Error submitting order: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
      if (localStorage.getItem("couponDetails")) {
        localStorage.removeItem("couponDetails");
      }
    }
  };
  const defaultBillingAddress = addresses.find(
    (address) => address.id === selectedBillingId
  );
  const defaultShippingAddress = addresses.find(
    (address) => address.id === selectedShippingId
  );
  const handleInputChange = (e) => {
    setCouponCode(e.target.value);
  };

  const handleCouponSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = await getCoupondetails(couponCode);
      setCouponDetails(data);
      localStorage.setItem("couponDetails", JSON.stringify(data));
      toast.success("Coupon applied successfully");
    } catch (error) {
      console.error("Error applying coupon:", error);
      const errorMessage = error.response.data.error;
      toast.error(errorMessage || "Error applying coupon");
    }
  };

  const handleRemoveCoupon = async (e) => {
    e.preventDefault();
    setCouponDetails(null);
    setCouponCode("");
    localStorage.removeItem("couponDetails");
  };
  const discount =
    couponDetails && couponDetails.couponType === "PERCENTAGE"
      ? total * (couponDetails.discountedValue / 100)
      : 0;
  if (!isCartLoaded) {
    return <div>Loading...</div>;
  }
  const kokoTax =
    selectedPayment === "payment6" && kokoTaxRate !== null
      ? (total * kokoTaxRate) / 100 // applied to products-only total
      : 0;
  // The applied coupon is already shown as a ticket above the list.
  const otherCoupons = availableCoupons.filter(
    (coupon) => coupon.couponId !== couponDetails?.couponId
  );

  const renderAddress = (address, kind, onChange) => (
    <div className="bm-addr">
      <div>
        <span className="bm-addr__kind">{kind}</span>
        <div className="bm-addr__name">
          {address.firstName} {address.lastName}
        </div>
        <p className="bm-addr__text">
          {[
            address.company,
            address.address1,
            address.address2,
            address.city,
            address.state,
            address.postCode,
          ]
            .filter(Boolean)
            .join(", ")}
        </p>
        <p className="bm-addr__text">
          {[address.phone, address.phone2, address.email]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <button type="button" className="btn btn-outline-main btn-sm rounded-8 flex-shrink-0" onClick={onChange}>
        Change
      </button>
    </div>
  );

  return (
    <section className="bm-shop">
      <Seo title="Checkout" noindex />
      <div className="bm-head">
        <h1 className="bm-title">Checkout</h1>
        <CheckoutSteps current={1} />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="bm-grid">
          <div className="bm-stack">
            <div className="bm-card">
              <div className="bm-card__head">
                <h2 className="bm-card__title">
                  Delivery address
                </h2>
                <button
                  type="button"
                  className="bm-link"
                  onClick={() => setShowAddressModal(true)}
                >
                  <i className="ph-bold ph-plus" />
                  Add new address
                </button>
              </div>

              {defaultBillingAddress ? (
                renderAddress(defaultBillingAddress, "Billing", () =>
                  setShowBillingSelectModal(true)
                )
              ) : (
                <div className="bm-empty">
                  No billing address found. Please add a new address.
                </div>
              )}

              {!useSameAddress &&
                (defaultShippingAddress ? (
                  renderAddress(defaultShippingAddress, "Shipping", () =>
                    setShowShippingSelectModal(true)
                  )
                ) : (
                  <div className="bm-empty" style={{ marginTop: 12 }}>
                    No shipping address found. Please add a new address.
                  </div>
                ))}

              <label className="bm-check">
                <input
                  type="checkbox"
                  checked={useSameAddress}
                  onChange={handleUseSameAddressChange}
                />
                Use billing address as shipping address
              </label>
            </div>

            {isAuthenticated && items.length > 0 && (
              <div className="bm-card">
                <div className="bm-card__head">
                  <h2 className="bm-card__title">
                    Coupons
                  </h2>
                </div>
                {couponDetails === null ? (
                  <CouponField
                    value={couponCode}
                    onChange={handleInputChange}
                    onApply={handleCouponSubmit}
                    applyDisabled={couponCode.length === 0}
                  />
                ) : (
                  <CouponTicket
                    coupon={couponDetails}
                    onRemove={handleRemoveCoupon}
                  />
                )}

                {otherCoupons.length > 0 && (
                  <>
                    <p className="bm-cticket-list__title">Available coupons</p>
                    <div className="bm-cticket-list">
                      {otherCoupons.map((coupon) => (
                        <CouponTicket
                          key={coupon.couponId}
                          coupon={coupon}
                          onSelect={() => {
                            setCouponDetails(coupon);
                            localStorage.setItem(
                              "couponDetails",
                              JSON.stringify(coupon)
                            );
                          }}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            <div className="bm-card">
              <div className="bm-card__head">
                <h2 className="bm-card__title">
                  Order notes
                </h2>
              </div>
              <textarea
                className="bm-input"
                placeholder="Special notes for delivery, e.g. landmarks or preferred time."
                value={customerNote}
                onChange={handleCustomerNoteChange}
              />
            </div>
          </div>

          <aside className="bm-stack">
            <div className="bm-card">
              <div className="bm-card__head">
                <h2 className="bm-card__title">
                  Your order
                </h2>
              </div>
              <div className="bm-mini">
                {items.map((item) => {
                  const effectivePrice = getEffectivePrice(item);
                  return (
                    <div className="bm-mini__item" key={item.id}>
                      <Link
                        to={`/product-details/${item.slug}`}
                        className="bm-mini__thumb"
                      >
                        <img src={S3_URL_PRODUCTS + item.images[0]} alt={item.name} />
                        <span className="bm-mini__qty">{item.quantity}</span>
                      </Link>
                      <Link
                        to={`/product-details/${item.slug}`}
                        className="bm-mini__name"
                      >
                        {item.name}
                      </Link>
                      <span className="bm-mini__price">
                        LKR {formatPrice(effectivePrice * item.quantity)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="bm-lines">
                <div className="bm-line">
                  <span>Subtotal</span>
                  <span>LKR {formatPrice(total)}</span>
                </div>
                {couponDetails && (
                  <div className="bm-line bm-line--discount">
                    <span>Discount ({couponDetails.couponCode})</span>
                    <span>-LKR {formatPrice(discount)}</span>
                  </div>
                )}
                <div className="bm-line">
                  <span>Delivery Fee</span>
                  <DeliveryFeeAmount
                    fee={deliveryFee}
                    status={deliveryFeeStatus}
                    onRetry={loadDeliveryFee}
                  />
                </div>
                {kokoTax > 0 && (
                  <div className="bm-line">
                    <span>Koko Tax</span>
                    <span>LKR {formatPrice(kokoTax)}</span>
                  </div>
                )}
              </div>
              <div className="bm-total">
                <span className="bm-total__label">Total</span>
                <span className="bm-total__value">
                  {deliveryFee === null
                    ? "—"
                    : `LKR ${formatPrice(total + deliveryFee - discount + kokoTax)}`}
                </span>
              </div>
            </div>

            <div className="bm-card">
              <div className="bm-card__head">
                <h2 className="bm-card__title">
                  Payment
                </h2>
              </div>
              <PaymentOptions
                kokoTaxRate={kokoTaxRate}
                kokoTaxRateStatus={kokoTaxRateStatus}
                hasNonCodItem={hasNonCodItem}
                selectedPayment={selectedPayment}
                selectedCardPayment={selectedCardPayment}
                isCardPaymentsExpanded={isCardPaymentsExpanded}
                onPaymentChange={handlePaymentChange}
                onCardPaymentChange={handleCardPaymentChange}
                onSelectCardPayments={() => {
                  setSelectedPayment("cardPayments");
                  setIsCardPaymentsExpanded(true);
                  setSelectedCardPayment("payment5"); // Auto-select OnePay
                }}
              />

              <button type="submit" className="btn btn-main w-100 rounded-8 mt-16 py-14 flex-center gap-8" disabled={isSubmitting}>
                {isSubmitting && (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                {isSubmitting ? "Placing order..." : "Place order"}
                {!isSubmitting && <i className="ph-bold ph-arrow-right" />}
              </button>
              <div className="bm-secure">
                <i className="ph-fill ph-lock-simple" />
                Secure checkout
              </div>
              <p className="bm-legal">
                Your personal data will be used to process your order, support
                your experience throughout this website, and for other purposes
                described in our <Link to="/privacy-policy">privacy policy</Link>.
              </p>
            </div>
          </aside>
        </div>
      </form>

      {/* Add New Address Modal */}
      {showAddressModal && (
        <CheckoutAddAddressModal
          isOpen={showAddressModal}
          onClose={() => {
            setShowAddressModal(false);
            // Refresh addresses after adding a new one
            getAddresses().then((res) => {
              if (res) {
                setAddresses(res.addresses || []);
                setSelectedShippingId(res.defaultShippingId);
                setSelectedBillingId(res.defaultBillingId);
              }
            });
          }}
        />
      )}
      {/* Billing Address Selection Modal */}
      {showBillingSelectModal && (
        <SelectAddressModal
          addresses={addresses}
          selectedAddressId={selectedBillingId}
          onSelect={(id) => {
            setSelectedBillingId(id);
            if (useSameAddress) setSelectedShippingId(id);
            setShowBillingSelectModal(false);
          }}
          onClose={() => setShowBillingSelectModal(false)}
          title="Select Billing Address"
        />
      )}
      {/* Shipping Address Selection Modal */}
      {showShippingSelectModal && (
        <SelectAddressModal
          addresses={addresses}
          selectedAddressId={selectedShippingId}
          onSelect={(id) => {
            setSelectedShippingId(id);
            setShowShippingSelectModal(false);
          }}
          onClose={() => setShowShippingSelectModal(false)}
          title="Select Shipping Address"
        />
      )}
    </section>
  );
};

export default RegisteredCheckout;
